import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Users, Lock, CheckCircle2, Clock, ShoppingCart } from "lucide-react";
import { api, resolveImg, fmtMoney } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PaymentDialog } from "@/components/PaymentDialog";

export default function InfoDetailPage() {
  const { id } = useParams();
  const { site } = useAuth();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [payOpen, setPayOpen] = useState(false);
  const cur = site?.currency || "₺";

  const load = useCallback(() => {
    api.get(`/infos/${id}`).then((r) => setInfo(r.data)).catch(() => setError("Bilgi bulunamadı."));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const t = setInterval(() => {
      api.post(`/infos/${id}/view`).then((r) => setInfo((p) => (p ? { ...p, viewers: r.data.viewers } : p))).catch(() => {});
    }, 30000);
    return () => clearInterval(t);
  }, [id]);

  if (error) return <div className="max-w-3xl mx-auto px-4 py-24 text-center text-slate-400" data-testid="info-not-found">{error} <Link to="/" className="text-cyan-400 underline">Ana sayfa</Link></div>;
  if (!info) return <div className="max-w-5xl mx-auto px-4 py-16"><div className="h-72 rounded-2xl bg-slate-900/60 animate-pulse" /></div>;

  const locked = !info.purchased;
  const fakeLines = Math.max(6, Math.min(14, Math.ceil((info.content_length || 400) / 70)));

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-10" data-testid="info-detail-page">
      <Link to="/" data-testid="detail-back-link" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-cyan-300 transition-colors mb-6"><ArrowLeft className="h-4 w-4" /> Paketlere dön</Link>

      <div className="relative rounded-3xl overflow-hidden border border-slate-800 bg-slate-900 aspect-[21/9] cx-fade-up">
        {info.image_url && <img src={resolveImg(info.image_url)} alt={info.title} className="absolute inset-0 h-full w-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-[#05070D] via-[#05070D]/50 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8">
          {info.category && <span className="font-mono-cx text-[10px] uppercase tracking-widest px-2 py-1 rounded-md bg-slate-950/80 border border-cyan-500/40 text-cyan-300">{info.category}</span>}
          <h1 data-testid="info-detail-title" className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-50 mt-3">{info.title}</h1>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3 cx-fade-up" style={{ animationDelay: "80ms" }}>
        <span data-testid="detail-viewers" className="cx-glass rounded-xl px-4 py-2 text-sm inline-flex items-center gap-2 text-slate-200"><span className="cx-live-dot" /> Şu anda <b className="text-cyan-300">{info.viewers}</b> kişi inceliyor</span>
        <span data-testid="detail-buyers" className="cx-glass rounded-xl px-4 py-2 text-sm inline-flex items-center gap-2 text-slate-200"><Users className="h-4 w-4 text-cyan-400" /> <b className="text-cyan-300">{info.buyers_count}</b> kişi satın aldı</span>
        <span data-testid="detail-price" className="ml-auto font-display text-2xl font-bold text-cyan-300">{info.is_free ? "ÜCRETSİZ" : fmtMoney(info.price, cur)}</span>
      </div>

      <section className="mt-10 cx-fade-up" style={{ animationDelay: "160ms" }}>
        <p className="font-mono-cx text-[10px] uppercase tracking-[0.3em] text-cyan-400 mb-3">Önizleme</p>
        <div className="cx-glass rounded-2xl p-6 sm:p-8">
          <p data-testid="info-preview-text" className="text-slate-200 leading-relaxed whitespace-pre-wrap text-base">{info.preview || "Önizleme metni eklenmemiş."}</p>
        </div>
      </section>

      <section className="mt-10 cx-fade-up" style={{ animationDelay: "240ms" }}>
        <div className="flex items-center justify-between mb-3">
          <p className="font-mono-cx text-[10px] uppercase tracking-[0.3em] text-cyan-400">Tam İçerik</p>
          {info.purchased ? (
            <span className="inline-flex items-center gap-1.5 text-xs text-emerald-300"><CheckCircle2 className="h-4 w-4" /> Erişim açık</span>
          ) : info.payment_status === "pending" ? (
            <span data-testid="payment-pending-badge" className="inline-flex items-center gap-1.5 text-xs text-amber-300"><Clock className="h-4 w-4" /> Ödeme onayı bekleniyor</span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-xs text-slate-400"><Lock className="h-4 w-4" /> Kilitli</span>
          )}
        </div>
        <div className="relative rounded-2xl border border-slate-800 bg-[#0A0E1A] overflow-hidden">
          <div className={`p-6 sm:p-8 ${locked ? "cx-blur-content" : ""}`} data-testid="info-hidden-content">
            {locked ? (
              <div className="space-y-3">
                {Array.from({ length: fakeLines }).map((_, i) => (
                  <p key={i} className="text-slate-300 text-base">{"Bu içerik satın alma sonrası görüntülenebilir. ".repeat(1 + (i % 3))}</p>
                ))}
              </div>
            ) : (
              <>
                <p className="text-slate-100 leading-relaxed whitespace-pre-wrap text-base">{info.content}</p>
                {info.content_images?.length > 0 && (
                  <div className="mt-8 grid sm:grid-cols-2 gap-4" data-testid="info-content-images">
                    {info.content_images.map((u, i) => (
                      <a key={i} href={resolveImg(u)} target="_blank" rel="noreferrer" className="block rounded-2xl overflow-hidden border border-slate-800 hover:border-cyan-500/50 transition-colors">
                        <img src={resolveImg(u)} alt={`İçerik ${i + 1}`} className="w-full h-64 object-cover" />
                      </a>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
          {locked && (
            <div className="absolute inset-0 flex items-center justify-center bg-[#05070D]/40 p-6">
              <div className="cx-glass rounded-2xl p-6 sm:p-8 text-center max-w-sm w-full shadow-[0_0_60px_-20px_rgba(0,240,255,0.5)]">
                <div className="h-12 w-12 rounded-xl bg-slate-900 border border-cyan-500/40 flex items-center justify-center mx-auto"><Lock className="h-5 w-5 text-cyan-300" /></div>
                <h3 className="font-display text-xl font-bold text-slate-50 mt-4">İçerik Kilitli</h3>
                <p className="text-sm text-slate-400 mt-2">Tam içeriği görmek için satın al. Ödemen onaylandığında burası otomatik açılır.</p>
                <p className="font-display text-3xl font-extrabold text-cyan-300 mt-4">{fmtMoney(info.price, cur)}</p>
                {info.payment_status === "pending" ? (
                  <div data-testid="payment-pending-overlay" className="mt-5 h-11 rounded-xl bg-amber-950/70 border border-amber-500/50 text-amber-200 text-sm font-semibold flex items-center justify-center gap-2"><Clock className="h-4 w-4" /> Onay bekleniyor</div>
                ) : (
                  <button data-testid="buy-button" onClick={() => setPayOpen(true)} className="mt-5 w-full h-12 rounded-xl cx-btn-primary text-sm inline-flex items-center justify-center gap-2"><ShoppingCart className="h-4 w-4" /> SATIN AL</button>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      <PaymentDialog open={payOpen} onOpenChange={setPayOpen} info={info} onSubmitted={load} />
    </div>
  );
}
